import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Clock, Target, CheckCircle2, Flame, Gauge, CalendarDays, TrendingDown, TrendingUp } from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";

const NAVY = "hsl(var(--brand-navy))";
const ORANGE = "hsl(var(--brand-orange))";

function fmtMinutes(m) {
  if (!m) return "0m";
  const h = Math.floor(m / 60);
  const r = Math.round(m % 60);
  return h ? `${h}h ${r}m` : `${r}m`;
}

function Kpi({ icon: Icon, label, value, hint, testId }) {
  return (
    <Card className="border-border/70">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-1 font-display text-2xl font-bold" data-testid={testId}>{value}</div>
            {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--brand-navy))]/10 text-[hsl(var(--brand-navy))]">
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children, testId, className = "" }) {
  return (
    <Card className={`border-border/70 ${className}`} data-testid={testId}>
      <CardContent className="p-5">
        <h3 className="mb-4 font-display text-base font-bold">{title}</h3>
        {children}
      </CardContent>
    </Card>
  );
}

function Empty({ text }) {
  return <div className="flex h-56 items-center justify-center rounded-lg border border-dashed text-xs text-muted-foreground">{text}</div>;
}

function KpiGrid({ s }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Kpi icon={Clock} label="Time spent" value={fmtMinutes(s.total_time_minutes)} hint={`${s.avg_time_per_question_sec}s / question`} testId="kpi-time-spent" />
      <Kpi icon={Target} label="Accuracy" value={`${s.accuracy}%`} hint={`${s.questions_answered} answered`} testId="kpi-accuracy" />
      <Kpi icon={CheckCircle2} label="Completion" value={`${s.completion_rate}%`} hint={`${s.sessions_completed}/${s.sessions_started} tests`} testId="kpi-completion" />
      <Kpi icon={Flame} label="Streak" value={`${s.current_streak_days}d`} hint="consecutive days" testId="kpi-streak" />
      <Kpi icon={CalendarDays} label="Active days" value={s.active_days_window} hint="in selected window" testId="kpi-active-days" />
      <Kpi icon={Gauge} label="Tests done" value={s.sessions_completed} hint={s.last_active ? `last ${new Date(s.last_active).toLocaleDateString()}` : "no activity"} testId="kpi-tests-done" />
    </div>
  );
}

function TopicList({ items, tone, empty, testId, linkable }) {
  if (!items.length) return <div data-testid={testId} className="rounded-lg border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">{empty}</div>;
  return (
    <div className="space-y-3" data-testid={testId}>
      {items.map((t, i) => (
        <div key={`${t.subject}-${t.topic}`} data-testid={`${testId}-item-${i}`}>
          <div className="mb-1 flex items-center justify-between gap-2 text-sm">
            <div className="min-w-0">
              <div className="truncate font-medium">{t.topic}</div>
              <div className="truncate text-xs text-muted-foreground">{t.subject} · {t.correct}/{t.total} correct</div>
            </div>
            <span className={`font-mono text-xs font-semibold ${tone === "weak" ? "text-rose-600" : "text-emerald-600"}`}>{t.accuracy}%</span>
          </div>
          <Progress value={t.accuracy} className="h-1.5" />
        </div>
      ))}
      {linkable && (
        <Link to="/quiz/new?mode=revision" className="inline-block pt-1 text-xs font-semibold text-[hsl(var(--brand-orange))] hover:underline" data-testid="weak-topics-revise-link">
          Revise mistakes now →
        </Link>
      )}
    </div>
  );
}

function ActivityCharts({ data }) {
  const daily = data.daily.map((d) => ({ ...d, label: d.date.slice(5) }));
  const hasDaily = daily.some((d) => d.attempts);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <ChartCard title="Daily study time & questions" testId="chart-daily-activity">
        {hasDaily ? (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="minutes" name="Minutes" stroke={NAVY} fill={NAVY} fillOpacity={0.15} />
              <Area type="monotone" dataKey="questions" name="Questions" stroke={ORANGE} fill={ORANGE} fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        ) : <Empty text="No activity in this window yet." />}
      </ChartCard>
      <ChartCard title="Score trend (last 20 tests)" testId="chart-score-trend">
        {data.score_trend.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.score_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="n" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v}%`} labelFormatter={(n, p) => p?.[0] ? `${p[0].payload.date} · ${p[0].payload.mode}` : n} />
              <Line type="monotone" dataKey="percentage" name="Score" stroke={ORANGE} strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <Empty text="Complete a test to see your trend." />}
      </ChartCard>
    </div>
  );
}

function SubjectCharts({ data }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <ChartCard title="Subject strength radar" testId="chart-subject-radar">
        {data.subjects.length >= 3 ? (
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={data.subjects} outerRadius="75%">
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
              <Radar dataKey="accuracy" name="Accuracy" stroke={NAVY} fill={NAVY} fillOpacity={0.3} />
              <Tooltip formatter={(v) => `${v}%`} />
            </RadarChart>
          </ResponsiveContainer>
        ) : data.subjects.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.subjects}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="subject" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="accuracy" fill={NAVY} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty text="Attempt questions across subjects to build your radar." />}
      </ChartCard>
      <ChartCard title="Test completion by mode" testId="chart-mode-completion">
        {data.modes.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.modes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="mode" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="started" name="Started" fill="#cbd5e1" radius={[6, 6, 0, 0]} />
              <Bar dataKey="completed" name="Completed" fill={ORANGE} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty text="No tests started yet." />}
      </ChartCard>
    </div>
  );
}

export function AnalyticsView({ data, linkable = false }) {
  return (
    <div className="space-y-6" data-testid="analytics-view">
      <KpiGrid s={data.summary} />
      <ActivityCharts data={data} />
      <SubjectCharts data={data} />
      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title={<span className="flex items-center gap-2"><TrendingDown className="h-4 w-4 text-rose-600" />Weak topics (below 60%)</span>}>
          <TopicList items={data.weak_topics} tone="weak" testId="weak-topics" linkable={linkable && data.weak_topics.length > 0}
            empty="No weak topics yet — need at least 3 answers per chapter." />
        </ChartCard>
        <ChartCard title={<span className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" />Strong topics (75%+)</span>}>
          <TopicList items={data.strong_topics} tone="strong" testId="strong-topics" empty="Keep practicing to unlock strong topics." />
        </ChartCard>
      </div>
    </div>
  );
}
